
export interface CacheOptions {
    duration: number;
    jitter?: number; // to avoid cache entries expiring at the same time, can add some jitter
}

export class Cache<T> {
    constructor(private readonly options: CacheOptions) {}

    private readonly map = new Map<string, { value: T, expires: Date }>();
    get(id: string) {
        const entry = this.map.get(id);
        if (entry?.expires && entry.expires.getTime() > Date.now()) return entry.value;
        return undefined;
    }

    set(id: string, value: T) {
        const expires = new Date(Date.now() + this.options.duration + Math.random() * (this.options.jitter ?? 0));
        this.map.set(id, { value, expires });
        return value;
    }

    clear(id: string) {
        this.map.delete(id);
    }

    /**
     * Drops expired entries. Nothing else evicts, so a long-lived cache of many keys must call this.
     * @returns The number of entries dropped.
     */
    prune(): number {
        const now = Date.now();
        let dropped = 0;
        for (const [id, entry] of this.map) {
            if (entry.expires.getTime() <= now) {
                this.map.delete(id);
                dropped++;
            }
        }
        return dropped;
    }

    /** Number of entries currently retained, expired or not. */
    get size(): number {
        return this.map.size;
    }
}
